import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import * as signalR from '@microsoft/signalr';
import axios from 'axios';
import axiosInstance from '../../../main/initializers/axios';
import JwtManager from '../../../main/utils/jwtManager';
import useGetUser from '../../../main/hooks/useGetUser';

/* ===================== TYPES ===================== */

export interface Notification {
  id: number;
  title?: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'mention' | 'like' | 'comment';
  unread: boolean;
  createdAt: string;
  readAt?: string;
  link?: string;
  avatar?: string;
  fromUser?: string;
  entityType?: string;
}

export interface ApiNotification {
  id: number;
  title?: string;
  message: string;
  type?: string;
  entityId?: number;
  entityType?: string;
  severity?: string;
  audienceType?: string;
  unread: boolean;
  createdAt: string;
  readAt?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (msg: string, type?: Notification['type'], link?: string, fromUser?: string) => void;
  markAllAsRead: () => Promise<void>;
  fetchNotifications: (onlyUnread?: boolean, page?: number, pageSize?: number) => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  clearAll: () => void;
  playNotificationSound: () => void;
  showNotificationToast: (notification: Notification) => void;
}

/* ===================== CONTEXT ===================== */

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const silentNotificationApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

/* ===================== API ===================== */

export const getNotifications = (onlyUnread = false, page = 1, pageSize = 20) =>
  axiosInstance.get<ApiNotification[]>(`/api/notification?onlyUnread=${onlyUnread}&page=${page}&pageSize=${pageSize}`);

export const markNotificationAsRead = (id: number) => axiosInstance.post(`/api/notification/read?id=${id}`);
export const markAllNotificationsAsRead = () => axiosInstance.post(`/api/notification/read-all`);
export const getUnreadNotificationCount = () =>
  silentNotificationApi.get<{ unreadCount: number }>(`/api/notification/count`, {
    headers: JwtManager.accessToken ? { Authorization: `Bearer ${JwtManager.accessToken}` } : undefined,
  });

/* ===================== PROVIDER ===================== */

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const user = useGetUser();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const companyId = user?.companyId ?? user?.CompanyId;

  /* ===================== HELPERS ===================== */

  const mapApiToNotification = (n: ApiNotification): Notification => {
    let link: string | undefined;
    const mappedType = ((n.type ?? n.severity ?? 'info').toLowerCase() as Notification['type']);

    if (n.entityType === 'Post' && n.entityId) {
      link = `/posts/${n.entityId}`;
    }

    return {
      id: n.id,
      title: n.title,
      message: n.message,
      type: mappedType,
      unread: n.unread,
      createdAt: n.createdAt,
      readAt: n.readAt,
      link,
      entityType: n.entityType,
    };
  };

  /* ===================== ACTIONS ===================== */

  const fetchNotifications = async (onlyUnread = false, page = 1, pageSize = 20) => {
    try {
      const res = await getNotifications(onlyUnread, page, pageSize);
      const mapped = res.data.map(mapApiToNotification);
      setNotifications(mapped);
      setUnreadCount(mapped.filter((n) => n.unread).length);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await getUnreadNotificationCount();
      setUnreadCount(res.data?.unreadCount ?? 0);
    } catch (err) {
      console.error('Failed to fetch notification count', err);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false, readAt: new Date().toISOString() } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, unread: false, readAt: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read', err);
    }
  };

  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const addNotification = (msg: string, type: Notification['type'] = 'info', link?: string, fromUser?: string) => {
    const newNotif: Notification = {
      id: Date.now(),
      title: undefined,
      message: msg,
      type,
      unread: true,
      createdAt: new Date().toISOString(),
      link,
      fromUser,
      avatar: fromUser?.charAt(0),
    };

    setNotifications((prev) => [newNotif, ...prev]);
    setUnreadCount((prev) => prev + 1);

    if (['mention', 'like', 'comment'].includes(type)) {
      playNotificationSound();
    }

    showNotificationToast(newNotif);
  };

  /* ===================== UI HELPERS ===================== */

  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      gainNode.gain.value = 0.1;

      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch {
    }
  };

  const showNotificationToast = (notification: Notification) => {
    const toast = document.createElement('div');
    toast.innerText = notification.message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: white;
      padding: 12px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 9999;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 4000);
  };

  useEffect(() => {
    if (!companyId || !JwtManager.accessToken) {
      setUnreadCount(0);
      return;
    }

    let mounted = true;
    let retryStartId: ReturnType<typeof setTimeout> | null = null;
    let connection: signalR.HubConnection | null = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_API_URL}hubs/notification`, {
        accessTokenFactory: () => JwtManager.accessToken ?? '',
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .build();

    const refreshUnreadCount = async () => {
      if (!mounted) return;
      await fetchUnreadCount();
    };

    const onVisibilityRefresh = () => {
      if (document.visibilityState === 'visible') {
        void refreshUnreadCount();
      }
    };

    connection.on('companyNotification', async (payload?: { companyId?: number }) => {
      if (!mounted) return;
      if (payload?.companyId && payload.companyId !== companyId) return;
      setUnreadCount((prev) => prev + 1);
      await refreshUnreadCount();
    });

    const scheduleStart = () => {
      if (!mounted) return;
      if (retryStartId) {
        clearTimeout(retryStartId);
      }
      retryStartId = setTimeout(() => {
        void startConnection();
      }, 5000);
    };

    const startConnection = async () => {
      if (!mounted || !connection) return;

      try {
        if (connection.state === signalR.HubConnectionState.Connected || connection.state === signalR.HubConnectionState.Connecting) {
          return;
        }

        await connection.start();
        await connection.invoke('JoinCompany', companyId);
        await refreshUnreadCount();
      } catch (err) {
        if ((err as any)?.name !== 'AbortError') {
          console.error('Failed to start notification hub', err);
        }
        scheduleStart();
      }
    };

    connection.onreconnected(async () => {
      if (!mounted || !connection) return;
      try {
        if (connection.state === signalR.HubConnectionState.Connected) {
          await connection.invoke('JoinCompany', companyId);
        }
        await refreshUnreadCount();
      } catch {
      }
    });

    connection.onclose(() => {
      scheduleStart();
    });

    void (async () => {
      await refreshUnreadCount();
      await startConnection();
    })();

    document.addEventListener('visibilitychange', onVisibilityRefresh);

    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVisibilityRefresh);
      if (retryStartId) {
        clearTimeout(retryStartId);
      }
      if (!connection) return;
      const closingConnection = connection;
      connection = null;
      void (async () => {
        try {
          if (closingConnection.state === signalR.HubConnectionState.Connected) {
            await closingConnection.invoke('LeaveCompany', companyId);
          }
        } catch {
        }

        try {
          await closingConnection.stop();
        } catch {
        }
      })();
    };
  }, [companyId]);

  /* ===================== PROVIDER ===================== */

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAllAsRead,
        fetchNotifications,
        markAsRead,
        clearAll,
        playNotificationSound,
        showNotificationToast,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

/* ===================== HOOK ===================== */

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};
